import React, { createContext, useCallback, useMemo } from 'react'
import {
  logEvent,
  executeWithRetry,
  normalizeError,
  checkStorageOptions,
} from '../../Utils'
import { ANALYTICS_TAGS } from '../../Constant'
import type { StorageContextType } from './types'
import { useCacheHelpers, useStorageReady } from '../../Hooks'
import { indexedDBs } from '../../Config/Storage/storage.config'

export const StorageContext = createContext<StorageContextType | null>(null)

export const StorageProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const {
    isReady: isStorageReady,
    storageStatuses,
    error: storageError,
    isChecking,
  } = useStorageReady()
  const { storeInCache, retrieveFromCache } = useCacheHelpers({
    dbName: indexedDBs.main.dbName,
    storeName: indexedDBs.main.storeName,
    dbVersion: indexedDBs.main.dbVersion,
    ttlMs: indexedDBs.main.ttlMs,
  })

  const store = useCallback(
    async (key: string, item: unknown, ttlMs?: number): Promise<void> => {
      if (!isStorageReady) {
        return
      }
      await executeWithRetry(async () => {
        await storeInCache(key, item, storageStatuses, ttlMs ?? indexedDBs.main.ttlMs)
        return true
      }, `store data with key: ${key}`)
    },
    [storageStatuses, storeInCache, isStorageReady, storageError]
  )

  const getRealTimeStorageStatus = () => {
    const status = checkStorageOptions()
    return {
      available: status.indexedDB || status.local || status.session,
      statuses: status,
    }
  }

  const storeWhenReady = useCallback(
    async (key: string, item: unknown, ttlMs: number = indexedDBs.main.ttlMs) => {
      const currentStatus = getRealTimeStorageStatus()

      if (currentStatus.available) {
        try {
          await storeInCache(key, item, currentStatus.statuses, ttlMs)
          return
        } catch (error) {
          console.error(`Storage failed for key: ${key}:`, error)
        }
      }

      // Exponential backoff if storage not ready
      const delays = [100, 200, 500, 1000, 2000]
      for (const delay of delays) {
        await new Promise((resolve) => setTimeout(resolve, delay))

        const retryStatus = getRealTimeStorageStatus()
        if (retryStatus.available) {
          try {
            await storeInCache(key, item, retryStatus.statuses, ttlMs)
            return
          } catch (error) {
            console.error(`Delayed storage failed for key: ${key}:`, error)
          }
        }
      }
    },
    [storeInCache]
  )

  const fetchAndCache = useCallback(
    async <T,>(
      key: string,
      fetcher: () => Promise<T>,
      onUpdate: (data: T) => void,
      ttlMs: number = indexedDBs.main.ttlMs
    ): Promise<void> => {
      let cacheServed = false

      // Immediate cache check with timeout
      if (isStorageReady) {
        try {
          const cachePromise = retrieveFromCache(key, storageStatuses, ttlMs)
          const timeoutPromise = new Promise<{ data: null; timeout: symbol }>(
            (resolve) =>
              setTimeout(() => resolve({ data: null, timeout: Symbol('timeout') }), 150)
          )

          const result = await Promise.race([cachePromise, timeoutPromise])
          if (result?.data) {
            onUpdate(result.data as T)
            cacheServed = true
          }
        } catch (error) {
          // Cache retrieval failed, continue with network
        }
      }

      // Always fetch fresh data
      try {
        const freshData = await fetcher()
        onUpdate(freshData)
        void storeWhenReady(key, freshData, ttlMs)
      } catch (error) {
        if (!cacheServed) {
          const normalizedError = normalizeError(error)
          logEvent(ANALYTICS_TAGS.network_error, {
            context: 'network_fetch_failed',
            key,
            error: normalizedError.message,
          })
        }
      }
    },
    [storageStatuses, retrieveFromCache, isStorageReady, storeWhenReady]
  )

  const ctx = useMemo<StorageContextType>(
    () => ({
      storageStatuses,
      isStorageReady,
      isStorageChecking: isChecking,
      storageError,
      fetchAndCache,
      store,
      retrieveFromCache,
    }),
    [
      storageStatuses,
      isStorageReady,
      isChecking,
      storageError,
      fetchAndCache,
      store,
      retrieveFromCache,
    ]
  )

  return <StorageContext.Provider value={ctx}>{children}</StorageContext.Provider>
}
